<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed, toRefs, nextTick } from 'vue';
import { createCurrencyFormatter, debounce } from '@/utils';
import { options } from '@/core/Options';
import { useDraggable } from '@vueuse/core';
import '@/assets/widget/coin.css';

// import CoinWidget from './components/CoinWidget.vue'
import CoinSprite from './CoinAnimation.vue';
// import CoinImg from './CoinImg.vue'
// Properly type the container ref
import CoinAnimation from './CoinAnimation.vue';
import CoinText from './CoinText.vue';
import CoinTooltip from './CoinTooltip.vue';
import { coinInfo, campaign } from '@/core/Model';
import { useTooltipManager } from '@/composables/useTooltipManager';
import { useI18n } from 'vue-i18n';
import { LobbyEvents } from '@/service/ExtEvents';
// import type { Campaign } from '@/types/types';

const { t } = useI18n();

const { formatShortCents, formatShortLocalCents } = createCurrencyFormatter({
	code: options?.rules?.currency?.code,
	symbol: options?.rules?.currency?.symbol,
	subunits: options?.rules?.currency?.subunits
});

const container = ref<HTMLDivElement | null>(null);

// Add container dimensions state
const containerBounds = ref({
	width: 0,
	height: 0
});

// Store coin position as percentages (0-100)
const coinPosition = ref({
	xPercent: 0, // initial position 10%
	yPercent: 0 // initial position 10%
});

// Single coin with dimensions and computed pixel positions
const COIN_SIZE = 186;
const props = defineProps<{
	enabled: boolean;
	//campaing: Campaign | null;
}>();

// Destructure props using toRefs to maintain reactivity
//const { enabled, campaing } = toRefs(props);
const { enabled } = toRefs(props);

// Animation delay constant in milliseconds
const COIN_ANIM_DELAY = 10000; // 10 seconds between animations
let coinAnimationTimer: number | null = null;

const scale = ref(0.7);
const iconScale = computed(() => 1.0);

const coin = ref({
	width: computed(() => COIN_SIZE * scale.value),
	height: computed(() => COIN_SIZE * scale.value)
});

// Computed pixel positions based on percentages
const coinPixelPosition = computed(() => ({
	x: (containerBounds.value.width - coin.value.width) * (coinPosition.value.xPercent / 100),
	y: (containerBounds.value.height - coin.value.height) * (coinPosition.value.yPercent / 100)
}));

const element = ref<HTMLDivElement | null>(null);

// Add padding state
const padding = ref({
	top: 100,
	right: 100,
	bottom: 100,
	left: 100
});

// Constants for padding
const PORTRAIT_PADDING_X = 45;
const PORTRAIT_PADDING_Y = 300;
const LANDSCAPE_PADDING_X = 50;
const LANDSCAPE_PADDING_Y = 100;

// Update the calculatePadding function to be more immediate
const calculatePadding = () => {
	const isPortrait = window.innerHeight > window.innerWidth;
	const gameScale = isPortrait
		? Math.min(window.innerHeight / 1920, window.innerWidth / 1080, 1)
		: Math.min(window.innerHeight / 1080, window.innerWidth / 1920, 1);

	// Immediately update scale
	scale.value = gameScale;

	// Calculate new padding values
	const newPadding = isPortrait
		? {
				left: PORTRAIT_PADDING_X * gameScale,
				right: PORTRAIT_PADDING_X * gameScale,
				top: PORTRAIT_PADDING_Y * gameScale,
				bottom: PORTRAIT_PADDING_Y * gameScale
			}
		: {
				left: LANDSCAPE_PADDING_X * gameScale,
				right: LANDSCAPE_PADDING_X * gameScale,
				top: LANDSCAPE_PADDING_Y * gameScale,
				bottom: LANDSCAPE_PADDING_Y * gameScale
			};

	// Update padding immediately
	padding.value = newPadding;
};

// Update container bounds and padding on mount and resize
const updateContainerBounds = () => {
	calculatePadding();
	if (container.value) {
		//const bounds = container.value.getBoundingClientRect();
		// const windowWidth = window.innerWidth;
		// const windowHeight = window.innerHeight;
		//window.getBoundingClientRect();
		containerBounds.value = {
			// width: bounds.width,
			// height: bounds.height
			width: window.innerWidth,
			height: window.innerHeight
		};
		// console.log('updateContainer bounds  ', containerBounds.value);

		// Add bounds checking after container size update
		adjustCoinPosition();
	}
	// console.log('updateContainer  ', container.value);
	// console.log('updateContainer Bounds ', containerBounds.value);
};

// Add debounced resize handler
const debouncedRecalculate = debounce(updateContainerBounds, 300);

// Add new function to adjust coin position if out of bounds
const adjustCoinPosition = () => {
	// Calculate maximum allowed percentages
	const maxXPercent = 100;
	const maxYPercent = 100;

	// Clamp position percentages between 0 and max
	coinPosition.value.xPercent = Math.min(Math.max(coinPosition.value.xPercent, 0), maxXPercent);
	coinPosition.value.yPercent = Math.min(Math.max(coinPosition.value.yPercent, 0), maxYPercent);

	// Update pixel positions through the computed property
	x.value = coinPixelPosition.value.x;
	y.value = coinPixelPosition.value.y;
};

const coinSprite = ref<InstanceType<typeof CoinSprite> | null>(null);

// Add the tooltip manager
const {
	showTooltip,
	init: initTooltip,
	run: runTooltip,
	stop: stopTooltip,
	pause: pauseTooltip
} = useTooltipManager({
	displayDuration: 3000,
	delay: 20000, // Single delay value for both initial and between tooltips
	maxRepeatCount: 3
	//debug vlaues
	// delay: 1000,
	// maxRepeatCount: 10000
});

// Add periodic animation function
const startPeriodicAnimation = () => {
	if (coinAnimationTimer) {
		window.clearTimeout(coinAnimationTimer);
	}

	coinAnimationTimer = window.setTimeout(() => {
		if (enabled.value && !isAnimating.value) {
			isAnimating.value = true;
			coinSprite.value?.run();
		}
	}, COIN_ANIM_DELAY);
};

const stopPeriodicAnimation = () => {
	if (coinAnimationTimer) {
		window.clearTimeout(coinAnimationTimer);
	}
};

// Update onMounted hook
onMounted(async () => {
	// console.log('COIN WIDGET onMounted');
	isAnimating.value = true;
	coinSprite.value?.run();
	await nextTick();

	updateContainerBounds();
	window.addEventListener('resize', debouncedRecalculate);

	assignTooltip();
	if (enabled.value) {
		// console.log('COIN WIDGET onMounted enabled.value', enabled.value);
		startPeriodicAnimation(); // Start periodic animation
	}
});

const assignTooltip = () => {
	if (campaign.value) {
		if (campaign.value.player_joined) {
			stopTooltip();
		} else {
			initTooltip(campaign.value.id || null);
		}
	}

	if (!enabled.value) {
		pauseTooltip();
	} else {
		runTooltip();
	}
};

// Update watch for enabled prop changes
watch(
	() => enabled.value,
	(newEnabled) => {
		// console.log('COIN WIDGET enabled changed:', newEnabled);

		if (newEnabled) {
			runTooltip();
			startPeriodicAnimation(); // Start periodic animation when enabled
			// nextTick(() => {
			// 	updateContainerBounds();
			// });
		} else {
			pauseTooltip();
			if (coinAnimationTimer) {
				window.clearTimeout(coinAnimationTimer);
				coinAnimationTimer = null;
			}
		}
	}
);

const isSingleCurrency = ref(false);

watch(campaign, (newVal) => {
	// console.log('COIN WIDGET campaign changed:', newVal);
	if (newVal) {
		isSingleCurrency.value = newVal.campaign_currency !== 'EUR';
	}
	assignTooltip();
});

watch(coinInfo, (newVal) => {
	if (newVal) {
		if (newVal.total_remaining_prizes > 0) {
			addState('leftPrizes');
		} else {
			removeState('leftPrizes');
		}
		if (newVal.max_prize > 0) {
			addState('maxWins');
		} else {
			removeState('maxWins');
		}
		//nextState()
		// if (!isAnimating.value){
		//   coinSprite.value?.run()
		// }
	}
});

onUnmounted(() => {
	stopPeriodicAnimation();
	window.removeEventListener('resize', debouncedRecalculate);
	stopTooltip();
});

// Setup draggable for the coin
const { x, y, isDragging } = useDraggable(element, {
	initialValue: { x: coinPixelPosition.value.x, y: coinPixelPosition.value.y },
	containerElement: container,
	preventDefault: false,
	stopPropagation: false,
	pointerTypes: ['mouse', 'touch', 'pen']
});

// Remove the manual isDragging ref since we're using it from useDraggable
const dragStartPosition = ref({ x: 0, y: 0 });

// Update these methods to remove isDragging management
const handlePointerDown = (e: PointerEvent) => {
	showTooltip.value = false;
	if (!enabled.value) return;
	isAnimating.value = false;
	coinSprite.value?.cancel();
	setState('dragIcon');
	stopPeriodicAnimation();

	//isAnimating.value = false;

	e.preventDefault();
	e.stopPropagation();
	dragStartPosition.value = { x: e.clientX, y: e.clientY };
};

const handlePointerMove = () => {
	// Empty handler but kept for event binding
};

// Simplified useStates composable
const useStates = (states: string[]) => {
	const currentStateIndex = ref(0);

	const nextState = () => {
		currentStateIndex.value = (currentStateIndex.value + 1) % states.length;
		return states[currentStateIndex.value];
	};

	const addState = (state: string) => {
		if (states.includes(state)) return;
		states.push(state);
	};

	const removeState = (state: string) => {
		const index = states.indexOf(state);
		if (index !== -1) {
			states.splice(index, 1);
		}
	};

	const setState = (state: string) => {
		currentStateIndex.value = states.indexOf(state);
	};

	const currentState = computed(() => states[currentStateIndex.value]);

	return { nextState, currentState, addState, removeState, setState };
};

// Update the states array
//const coinStates = ['bIcon', 'leftPrizes', 'maxWins', 'dragIcon']
const coinStates = ['bIcon', 'dragIcon'];

// Initialize the state management
const { nextState, currentState, addState, removeState, setState } = useStates(coinStates);

// Click handler
const openLobby = () => {
	stopTooltip();
	window.postMessage({ name: LobbyEvents.OPEN_LOBBY, eventType: 'drop', id: campaign.value?.id || '' }, '*');
};

const handleCoinClick = () => {
	if (!enabled.value || isAnimating.value) return;
	//nextState()
	//stopTooltip();
	openLobby();
};

// Update the handlePointerUp function
const handlePointerUp = (e: PointerEvent) => {
	if (!enabled.value) return;
	startPeriodicAnimation();
	e.preventDefault();
	e.stopPropagation();
	const dx = Math.abs(e.clientX - dragStartPosition.value.x);
	const dy = Math.abs(e.clientY - dragStartPosition.value.y);

	//updateContainerBounds();
	//adjustCoinPosition();

	// If movement was less than 5px in any direction and not dragging, consider it a click

	if (dx < 5 && dy < 5 && !isDragging.value) {
		setState('bIcon');
		handleCoinClick();
		if (!isAnimating.value) {
			isAnimating.value = true;
			coinSprite.value?.run();
		}
	}
};

// Update percentage positions when dragging
watch([x, y], ([newX, newY]) => {
	const maxX = containerBounds.value.width - coin.value.width;
	const maxY = containerBounds.value.height - coin.value.height;
	//const maxX = containerBounds.value.width + 1;
	//const maxY = containerBounds.value.height + 1;

	coinPosition.value.xPercent = (newX / maxX) * 100;
	coinPosition.value.yPercent = (newY / maxY) * 100;
	// console.log(
	// 	`container_width: ${containerBounds.value.width}`,
	// 	`newX ${newX}`,
	// 	`tooltipPosition ${tooltipPosition.value}`,
	// 	`xPercent ${coinPosition.value.xPercent}`,
	// 	`maxX ${maxX}`
	// );
});

//animation
const isAnimating = ref(false);

const handleAnimationEnd = () => {
	nextState();
	isAnimating.value = false;
	if (enabled.value) {
		startPeriodicAnimation(); // Restart the animation timer after animation ends
	}
};

// Add computed property for cursor style
const cursorStyle = computed(() => {
	if (!enabled.value) {
		return 'not-allowed';
	}
	if (isDragging.value) {
		return 'grabbing';
	}
	return 'default';
});

// Add new refs for tooltip
const tooltipPosition = computed(() => {
	// Show tooltip on the left if coin is in the right half of the container
	return coinPosition.value.xPercent > 50 ? 'left' : 'right';
});

// Update handleTooltipClick
const handleTooltipClick = () => {
	//pauseTooltip();
	openLobby();

	// Add your subscription logic here
};
</script>

<template>
	<div
		ref="container"
		class="pointer-events-none fixed z-[997]"
		:style="{
			top: `${padding.top}px`,
			right: `${padding.right}px`,
			bottom: `${padding.bottom}px`,
			left: `${padding.left}px`
		}"
	>
		<div
			v-if="showTooltip"
			class="tooltip-wrapper"
			:style="{
				position: 'absolute',
				transform: `translate(${x}px, ${y}px)`,
				width: `${coin.width}px`,
				height: `${coin.height}px`,
				pointerEvents: 'auto'
			}"
		>
			<CoinTooltip
				:key="tooltipPosition + '-' + x + '-' + y"
				:position="tooltipPosition"
				:height="element?.offsetHeight"
				@click="handleTooltipClick"
			/>
		</div>

		<div
			ref="element"
			class="coin-element"
			@pointerdown="handlePointerDown"
			@pointerup="handlePointerUp"
			@pointermove="handlePointerMove"
			:style="{
				position: 'absolute',
				transform: `translate(${x}px, ${y}px)`,
				cursor: cursorStyle,
				width: `${coin.width}px`,
				height: `${coin.height}px`,
				userSelect: 'none',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				transformOrigin: '0 0',
				pointerEvents: enabled ? 'auto' : 'none',
				transition: 'width 0.2s ease-out, height 0.2s ease-out',
				zIndex: 777,
				filter: enabled ? 'none' : 'grayscale(100%)',
				opacity: enabled ? 1 : 0.6
			}"
		>
			<CoinAnimation
				ref="coinSprite"
				:scale="scale"
				:style="{
					pointerEvents: 'none',
					touchAction: 'none',
					cursor: 'inherit'
				}"
				@animation_end="handleAnimationEnd"
			/>

			<Transition name="scale">
				<div v-if="!isAnimating" class="coin-state-container" :data-state="currentState">
					<!-- bIcon state -->
					<template v-if="currentState === 'bIcon'">
						<img
							src="@/assets/widget/B.png"
							alt="B icon"
							:style="{
								transform: `scale(${iconScale})`,
								width: '50%',
								height: '50%',
								objectFit: 'contain',
								transformOrigin: 'center',
								pointerEvents: 'none'
							}"
						/>
					</template>

					<!-- leftPrizes state -->
					<template v-else-if="currentState === 'leftPrizes'">
						<div class="prizes-overlay">
							<CoinText
								:value="coinInfo?.total_remaining_prizes || 0"
								:label="t('widget.coin_tip_left')"
							/>
						</div>
					</template>

					<!-- maxWins state -->
					<template v-else-if="currentState === 'maxWins'">
						<div class="prizes-overlay">
							<CoinText
								:value="
									isSingleCurrency
										? formatShortLocalCents(coinInfo?.max_prize || 0, true)
										: formatShortCents(coinInfo?.max_prize || 0, true)
								"
								:label="t('widget.coin_tip_win')"
							/>
						</div>
					</template>

					<!-- dragIcon state -->
					<template v-else-if="currentState === 'dragIcon'">
						<img
							src="@/assets/widget/arrows.png"
							alt="Drag icon"
							:style="{
								transform: `scale(${iconScale})`,
								width: '50%',
								height: '50%',
								objectFit: 'contain',
								transformOrigin: 'center',
								pointerEvents: 'none'
							}"
						/>
					</template>
				</div>
			</Transition>
		</div>
	</div>
</template>

<style scoped>
.scale-enter-active {
	animation: scale-in-out 0.4s ease-in-out;
}

.scale-leave-active {
	transition: all 0.1s ease-out;
}

.scale-leave-from {
	opacity: 1;
	transform: scale(v-bind(iconScale)) !important;
}

.scale-leave-to {
	opacity: 0;
	transform: scale(calc(v-bind(iconScale) * 0.9)) !important;
}

@keyframes scale-in-out {
	0% {
		opacity: 0;
		transform: scale(calc(v-bind(iconScale) * 0.9)) !important;
	}
	50% {
		opacity: 1;
		transform: scale(calc(v-bind(iconScale) * 1.2)) !important;
	}
	100% {
		opacity: 1;
		transform: scale(v-bind(iconScale)) !important;
	}
}

.coin-state-container {
	position: relative;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.prizes-overlay {
	position: absolute;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* Update state-specific animations */
.coin-state-container[data-state='leftPrizes'] .prizes-icon {
	animation: pulse 2s infinite;
}

@keyframes pulse {
	0% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
	100% {
		transform: scale(1);
	}
}

@keyframes wiggle {
	0%,
	100% {
		transform: translateX(0);
	}
	25% {
		transform: translateX(-3px);
	}
	75% {
		transform: translateX(3px);
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
